import { batchCreateRelationship, getNestedSubelementsByType, getNextUuid } from '@fs/zion-sls'
import zionDebug from '@fs/zion-debug'
import { preparePersonIdsAndPartitionKeys } from './relationshipKeys'

// To turn this on, in the browser console: localStorage.debug = 'fs:rip:restoreRelationships'
const debug = zionDebug('rip:restoreRelationships')

/**
 * Rebuilds the relationships held between people moving into the same group.
 *
 * A move deletes every relationship on the person moved, which is how ties to the group they leave
 * get broken. Ties between two people moving together are collateral — the same
 * `batchMovePersonToOtherRecord` call deletes and reparents, so no ordering avoids it — and the Add
 * Existing Person confirmation deliberately leaves those pairs off its list, so they have to come
 * back.
 *
 * @param {Object} params
 * @param {Array<Object>} params.movedPersons - Everyone moving, read before the move dispatches are
 *   applied. A counterpart missing from this list keeps their tie broken.
 * @param {string} params.partitionKey - Partition the rebuilt relationship elements belong to
 * @returns {Array<Object>} Relationship creation dispatches
 */
export function restoreRelationshipsAmongMovedPersons({ movedPersons = [], partitionKey }) {
  const dispatches = []
  const restoredRelationshipIds = new Set()

  movedPersons.forEach((movedPerson) => {
    getNestedSubelementsByType(movedPerson, 'RELATIONSHIP').forEach((relationshipEl) => {
      const otherEnd = relationshipEl.superElements?.find((superEl) => superEl.id !== movedPerson.id)
      // A counterpart left behind is the tie the move is meant to break.
      const otherPerson = otherEnd && movedPersons.find((candidate) => candidate.id === otherEnd.id)
      if (!otherPerson) {
        debug('leaving relationship broken, counterpart is not moving', {
          relationshipId: relationshipEl.id,
          relType: relationshipEl.relType,
          movedPersonId: movedPerson.id,
          counterpartId: otherEnd?.id,
        })
        return
      }

      // Both persons hold the same relationship element, so each pair is reached twice.
      if (restoredRelationshipIds.has(relationshipEl.id)) return
      restoredRelationshipIds.add(relationshipEl.id)

      const { person1Id, person2Id, person1PartitionKey, person2PartitionKey } = preparePersonIdsAndPartitionKeys({
        person: movedPerson,
        otherPerson,
        relationshipDirection: otherEnd.order,
      })

      debug('rebuilding relationship between two people moving in together', {
        originalRelationshipId: relationshipEl.id,
        relType: relationshipEl.relType,
        person1Id,
        person2Id,
      })

      dispatches.push(
        ...(batchCreateRelationship({
          partitionKey,
          person1ID: person1Id,
          person2ID: person2Id,
          person1PartitionKey,
          person2PartitionKey,
          relType: relationshipEl.relType,
          id: getNextUuid(),
        }) || [])
      )
    })
  })

  return dispatches
}

export default restoreRelationshipsAmongMovedPersons
